import { useMemo, memo } from "react";
import {
  Link,
  useParams,
  useLocation,
  useUserPermissions,
  useSidebarStore,
} from "@";

const SideBarLi = ({ name, to, children, canShow }) => {
  const { permissions } = useUserPermissions();
  const location = useLocation();
  const { closeSidebar } = useSidebarStore();
  const params = useParams();

  const isActive = useMemo(
    () =>
      [
        to,
        `${to}/create`,
        `${to}/reports`,
        `${to}/${params?.id || params?.show}`,
        `${to}/${params?.id || params?.edit}/edit`,
      ].some(
        (path) => location.pathname === path || location.pathname.match(path),
      ),
    [to, location.pathname, params?.id, params?.show, params?.edit],
  );

  const isHasPermission = useMemo(() => {
    if (canShow) {
      return canShow
        ?.split(",")
        ?.some((permission) => permissions?.[permission]);
    } else {
      return true;
    }
  }, [canShow, permissions]);

  return (
    isHasPermission && (
      <li
        data-popover-placement="left"
        data-popover-target={`popover-sidebar-${to}`}
        className="dropdown-sidebar relative mb-0.5"
      >
        <Link
          to={to}
          onClick={closeSidebar}
          className={`sidebar-li group relative flex items-center rounded-2xl px-3 py-2.5 text-[15px] font-medium transition-all duration-200 ease-out ${
            isActive
              ? "bg-gradient-to-l from-purple-50 via-purple-50/70 to-transparent text-purple-900 dark:from-purple-950/60 dark:via-purple-900/30 dark:to-transparent dark:text-purple-200"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/60 dark:hover:text-white"
          }`}
        >
          {isActive && (
            <span className="pointer-events-none absolute start-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-e-full bg-gradient-to-b from-purple-700 to-purple-950 shadow-[0_0_12px_theme(colors.purple.500/40)] dark:from-purple-400 dark:to-purple-600" />
          )}

          <span
            className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all duration-200 ${
              isActive
                ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800"
                : "bg-gray-50 text-gray-500 ring-1 ring-gray-100 group-hover:bg-purple-50 group-hover:text-purple-800 group-hover:ring-purple-100 dark:bg-gray-800/70 dark:text-gray-400 dark:ring-gray-700/60 dark:group-hover:bg-purple-900/30 dark:group-hover:text-purple-300"
            }`}
          >
            {children}
          </span>

          <span
            className={`title ms-3 tracking-tight ${
              isActive ? "font-semibold" : ""
            }`}
          >
            {name}
          </span>
        </Link>

        {/* Tooltip for collapsed mode — shown on parent hover */}
        <div
          data-popover
          id={`popover-sidebar-${to}`}
          role="tooltip"
          className="popover-sidebar invisible absolute z-[55] hidden w-fit rounded-2xl border border-gray-100 bg-white/95 text-sm text-gray-700 opacity-0 shadow-lg shadow-gray-900/5 backdrop-blur-xl transition-[opacity,transform] duration-200 dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-300 [.small_&]:start-[calc(100%+10px)] [.small_&]:top-1/2 [.small_&]:-translate-x-1 [.small_&]:-translate-y-1/2 rtl:[.small_&]:translate-x-1 [.small_.dropdown-sidebar:hover_&]:!visible [.small_.dropdown-sidebar:hover_&]:!block [.small_.dropdown-sidebar:hover_&]:translate-x-0 [.small_.dropdown-sidebar:hover_&]:!opacity-100 rtl:[.small_.dropdown-sidebar:hover_&]:translate-x-0"
        >
          <div className="rounded-2xl px-3 py-2">
            <h3 className="whitespace-nowrap font-semibold tracking-tight">
              {name}
            </h3>
          </div>
        </div>
      </li>
    )
  );
};

export default memo(SideBarLi);
